import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Toaster } from './components/ui/sonner';
import SEO from './components/SEO';
import StructuredData from './components/StructuredData';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import Values from './components/Values';
import Projects from './components/Projects';
import TerrainsOffers from './components/TerrainsOffers';
import Agencies from './components/Agencies';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';

// Pages secondaires chargées à la demande (hors bundle principal)
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'));
const CGU = lazy(() => import('./pages/CGU'));
const Histoire = lazy(() => import('./pages/Histoire'));
const CCMI = lazy(() => import('./pages/CCMI'));
const RE2020 = lazy(() => import('./pages/RE2020'));
const Realisations = lazy(() => import('./pages/Realisations'));
const NotFound = lazy(() => import('./pages/NotFound'));

// ScrollToTop component to handle route changes
function ScrollToTop() {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Au premier chargement, ne pas toucher au focus : le navigateur doit
    // démarrer en haut du document, sinon le lien d'évitement et la
    // navigation deviennent inatteignables en tabulation avant.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo(0, 0);
    // Changement de route : déplacer le focus annonce la nouvelle page
    // aux lecteurs d'écran.
    document.getElementById('main')?.focus();
  }, [pathname]);

  return null;
}

// HomePage component with all sections
function HomePage() {
  return (
    <>
      <SEO
        title="Bouhdel - Constructeur maisons individuelles sud France"
        description="Constructeur de maisons individuelles sur mesure dans le sud de la France. Arles, Nîmes, Avignon. Expertise depuis plus de 20 ans. Devis gratuit."
        url="https://www.bouhdel.com"
        type="website"
      />
      <StructuredData />
      <Header />
      <main id="main" tabIndex={-1}>
        <Hero />
        <Services />
        <Values />
        <Projects />
        <TerrainsOffers />
        <Agencies />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    // Détection mobile pour iOS
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Initialiser AOS avec configuration optimisée pour mobile (garde les animations)
    AOS.init({
      duration: 800,
      easing: 'ease-out-cubic',
      once: true,
      mirror: false,
      offset: isMobile ? 20 : 50,
      delay: 0,
      // Respecte le réglage système « réduire les animations »
      disable: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });

    // Chargement à froid sur une ancre (/#services) : rejoindre la section.
    // Sinon, démarrer en haut de page.
    const hash = window.location.hash;
    if (hash) {
      // Laisser le premier rendu se poser avant de mesurer la position.
      requestAnimationFrame(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/histoire" element={<Histoire />} />
            <Route path="/ccmi" element={<CCMI />} />
            <Route path="/re2020" element={<RE2020 />} />
            <Route path="/realisations" element={<Realisations />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Toaster />
      </div>
    </Router>
  );
}

export default App;
